import React, { useEffect, useRef, useState } from "react";
import "./VideoBackground.css";

const VideoBackground = () => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hlsLoaded, setHlsLoaded] = useState(false);
  const [logMessages, setLogMessages] = useState([]);

  // Add debug logging function
  const logDebug = (message) => {
    console.log(message);
    setLogMessages((prev) => [
      ...prev,
      `${new Date().toISOString()}: ${message}`,
    ]);
  };

  // Set background color immediately for better LCP
  useEffect(() => {
    const bgElement = document.querySelector(".video-background");
    if (bgElement) {
      bgElement.style.backgroundColor = "#241025";
    }

    // Check if HLS.js is already loaded
    if (window.Hls) {
      logDebug("HLS.js already loaded");
      setHlsLoaded(true);
    } else {
      logDebug("Waiting for HLS.js to load");
      // Set up a listener for when HLS.js loads
      const checkHls = setInterval(() => {
        if (window.Hls) {
          logDebug("HLS.js loaded");
          setHlsLoaded(true);
          clearInterval(checkHls);
        }
      }, 100);

      // Clear interval after a timeout
      setTimeout(() => {
        clearInterval(checkHls);
        if (!window.Hls) {
          logDebug("HLS.js failed to load after timeout");
        }
      }, 10000);

      return () => clearInterval(checkHls);
    }
  }, []);

  // Check for mobile device
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 480;
      if (mobile !== isMobile) {
        logDebug(`Device type changed: ${mobile ? "mobile" : "desktop"}`);
        setIsMobile(mobile);

        // Reinitialize player if device type changes
        if (isInitialized) {
          logDebug("Reinitializing player due to device type change");
          if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
          }

          if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.removeAttribute("src");
            videoRef.current.load();
          }

          setIsInitialized(false);
          setIsVisible(false);

          setTimeout(() => {
            initializePlayer();
          }, 100);
        }
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, [isMobile, isInitialized]);

  // Initialize player when HLS.js is loaded or mobile status changes
  useEffect(() => {
    if (!isInitialized && videoRef.current) {
      logDebug("Calling initializePlayer");
      initializePlayer();
    }
  }, [hlsLoaded, isMobile, isInitialized]);

  // Force direct styling on video element
  const forceCorrectStyles = () => {
    if (videoRef.current) {
      // Force critical styling for correct aspect ratio
      videoRef.current.style.setProperty("width", "100%", "important");
      videoRef.current.style.setProperty("height", "100%", "important");
      videoRef.current.style.setProperty("object-fit", "cover", "important");
      videoRef.current.style.setProperty(
        "object-position",
        "center center",
        "important"
      );
      videoRef.current.style.setProperty("position", "absolute", "important");
      videoRef.current.style.setProperty("top", "0", "important");
      videoRef.current.style.setProperty("left", "0", "important");
    }
  };

  // Ensure styling is applied after initialization and periodically
  useEffect(() => {
    if (videoRef.current && isInitialized) {
      // Apply immediately
      forceCorrectStyles();

      // Apply again after a delay
      const styleTimeout = setTimeout(forceCorrectStyles, 500);

      // And periodically for a few seconds to ensure it sticks
      const styleInterval = setInterval(forceCorrectStyles, 1000);
      setTimeout(() => clearInterval(styleInterval), 5000);

      return () => {
        clearTimeout(styleTimeout);
        clearInterval(styleInterval);
      };
    }
  }, [isInitialized, isVisible]);

  const initializePlayer = () => {
    if (!videoRef.current || isInitialized) return;

    // Select sources - for mobile, prefer WebM for smaller size
    const videoSource = isMobile
      ? "https://storage.googleapis.com/inaq_sk_bg_video/bg_inaq_astro_mobile.webm"
      : "https://storage.googleapis.com/inaq_sk_bg_video/bg_video_inaq.mp4";

    // Fallback MP4 for browsers that don't support WebM
    const mp4Fallback = isMobile
      ? "https://storage.googleapis.com/inaq_sk_bg_video/bg_inaq_astro_mobile.mp4"
      : "https://storage.googleapis.com/inaq_sk_bg_video/bg_inaq_astro.mp4";

    const hlsSource =
      "https://storage.googleapis.com/inaq_sk_bg_video/transcoded2/manifest.m3u8";

    // Configure video properties
    const video = videoRef.current;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.autoplay = true;

    // CRITICAL: Add poster image for faster LCP
    // Use mobile JPEG for all devices (smaller file size = faster LCP)
    video.poster = "https://storage.googleapis.com/inaq_sk_bg_video/bg_inaq_astro_mobile.jpg";

    // Add classes for CSS compatibility
    video.classList.add(
      "video-js",
      "vjs-fluid",
      "vjs-big-play-button-centered",
      "vjs-tech"
    );

    try {
      // For mobile, use WebM for smaller file size
      if (isMobile) {
        logDebug("Mobile device detected - using WebM source");
        video.src = videoSource;

        video.addEventListener("loadedmetadata", () => {
          logDebug("Mobile WebM metadata loaded");
          forceCorrectStyles(); // Apply correct styling again

          video.play().catch((e) => {
            logDebug(`Mobile WebM autoplay prevented: ${e.message}`);
          });

          setIsInitialized(true);
          setTimeout(() => setIsVisible(true), 100);
        });

        video.addEventListener("error", (e) => {
          logDebug(`Mobile WebM error: ${video.error?.message || "Unknown"}`);
          // Fallback to MP4 if WebM fails
          logDebug("Falling back to mobile MP4");
          video.src = mp4Fallback;
          video.play().catch((err) => {
            logDebug(`Fallback video autoplay prevented: ${err.message}`);
          });
        });
      }
      // For desktop, use HLS if available
      else if (window.Hls && window.Hls.isSupported()) {
        logDebug("Desktop with HLS support - using HLS.js");

        hlsRef.current = new window.Hls({
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
        });

        hlsRef.current.loadSource(hlsSource);
        hlsRef.current.attachMedia(video);

        hlsRef.current.on(window.Hls.Events.MANIFEST_PARSED, () => {
          logDebug("HLS manifest parsed successfully");
          forceCorrectStyles(); // Apply correct styling again

          video.play().catch((e) => {
            logDebug(`HLS autoplay prevented: ${e.message}`);
          });

          setIsInitialized(true);
          setTimeout(() => setIsVisible(true), 100);
        });

        hlsRef.current.on(window.Hls.Events.ERROR, (event, data) => {
          logDebug(`HLS error: ${data.type} - ${data.details}`);
          if (data.fatal) {
            logDebug("Fatal HLS error, falling back to MP4");
            video.src = videoSource;
            video.play().catch((e) => {
              logDebug(`Fallback autoplay prevented: ${e.message}`);
            });
          }
        });
      }
      // For Safari/iOS desktop which has native HLS support
      else if (
        !isMobile &&
        video.canPlayType("application/vnd.apple.mpegurl")
      ) {
        logDebug("Desktop Safari - using native HLS support");
        video.src = hlsSource;

        video.addEventListener("loadedmetadata", () => {
          logDebug("Native HLS metadata loaded");
          forceCorrectStyles(); // Apply correct styling again

          video.play().catch((e) => {
            logDebug(`Native HLS autoplay prevented: ${e.message}`);
          });

          setIsInitialized(true);
          setTimeout(() => setIsVisible(true), 100);
        });

        video.addEventListener("error", (e) => {
          logDebug(`Native HLS error: ${e.target.error?.message || "Unknown"}`);
          video.src = videoSource;
          video.play().catch((e) => {
            logDebug(`MP4 fallback prevented: ${e.message}`);
          });
        });
      }
      // Fallback to MP4 for all other desktop browsers
      else {
        logDebug("Desktop without HLS support - using direct MP4");
        video.src = videoSource;

        video.addEventListener("loadedmetadata", () => {
          logDebug("Desktop MP4 metadata loaded");
          forceCorrectStyles(); // Apply correct styling again

          video.play().catch((e) => {
            logDebug(`MP4 autoplay prevented: ${e.message}`);
          });

          setIsInitialized(true);
          setTimeout(() => setIsVisible(true), 100);
        });
      }

      // Add global error listener for debugging
      video.addEventListener("error", (e) => {
        logDebug(`Video error: ${video.error?.message || "Unknown error"}`);
        logDebug(`Current src: ${video.currentSrc}`);
      });

      // Log completed initialization
      logDebug(
        `Video initialization complete (${isMobile ? "mobile" : "desktop"} version)`
      );
    } catch (error) {
      logDebug(`Error initializing player: ${error.message}`);

      // Final fallback
      if (videoRef.current) {
        logDebug("Using emergency fallback");
        videoRef.current.src = isMobile ? mp4Fallback : videoSource;
        videoRef.current.play().catch((e) => {
          logDebug(`Emergency fallback prevented: ${e.message}`);
        });
        setIsInitialized(true);
        setTimeout(() => setIsVisible(true), 100);
      }
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      logDebug("Component cleanup");
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute("src");
        videoRef.current.load();
      }
    };
  }, []);

  return (
    <div className='video-background'>
      <div
        className={`video-container ${isVisible ? "visible" : ""}`}
        style={{
          opacity: isVisible ? 1 : 0,
          transition: "opacity 0.6s ease-in-out",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <div className='video-js'>
          <video
            ref={videoRef}
            playsInline
            muted
            className='vjs-tech'
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center center",
            }}
          />
        </div>
      </div>

      {/* Debug panel */}
      {process.env.NODE_ENV === "development" && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            right: 0,
            background: "rgba(0,0,0,0.7)",
            color: "white",
            padding: "10px",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 9999,
            fontSize: "10px",
            display: "none", // Change to 'none' to hide debugging
          }}
        >
          <div>Source: {videoRef.current?.currentSrc || "unknown"}</div>
          <div>Mobile: {isMobile ? "true" : "false"}</div>
          <div>Initialized: {isInitialized ? "true" : "false"}</div>
          <div>HLS loaded: {hlsLoaded ? "true" : "false"}</div>
          <div>Log (recent first):</div>
          <ul
            style={{
              maxHeight: "100px",
              overflowY: "auto",
              padding: "0 0 0 15px",
              margin: 0,
            }}
          >
            {logMessages
              .slice(-10)
              .reverse()
              .map((msg, i) => (
                <li key={i} style={{ fontSize: "9px", marginBottom: "2px" }}>
                  {msg}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default VideoBackground;
